import Quill from 'quill/core';
import { AlignStyle } from 'quill/formats/align';
import { BackgroundStyle } from 'quill/formats/background';
import Blockquote from 'quill/formats/blockquote';
import Bold from 'quill/formats/bold';
import { ColorStyle } from 'quill/formats/color';
import Header from 'quill/formats/header';
import Image from 'quill/formats/image';
import IndentClass from 'quill/formats/indent';
import Italic from 'quill/formats/italic';
import Link from 'quill/formats/link';
import List from 'quill/formats/list';
import Script from 'quill/formats/script';
import Strike from 'quill/formats/strike';
import Underline from 'quill/formats/underline';
import Toolbar from 'quill/modules/toolbar';
import SnowTheme from 'quill/themes/snow';
import { DisposalSet, select } from '@neptune/cx/utils';

// Emitted into common-vendor.css, which must be associated with a page or its
// master to load.
import 'quill/dist/quill.snow.css';

/**
 * Every format the editor can represent, registered name to implementation.
 *
 * Quill needs the same vocabulary in two places -- its registry and the editor's `formats` option
 * -- and anything missing from either is deleted from the document on load. Deriving both from one
 * map is what stops them drifting apart.
 *
 * Most of these are deliberately absent from the toolbar. They exist so that an imported post
 * keeps what it arrived with: a member cannot apply them, but editing a post no longer strips
 * them. `align`, `color` and `background` use the Style variants rather than Quill's default Class
 * ones, because the class variants are styled only inside `.ql-editor` -- saving through them
 * would round-trip cleanly in the editor and then render unstyled in the read view.
 *
 * Not registered, and so still lost on edit:
 * - `size`/`font`: the stock whitelists match none of the imported values, so registering them
 *   would either buy nothing or import Word's type scale into a themed page.
 * - `table`: Quill's table cannot represent colspan, rowspan, `<th>` or `<thead>`, and rewrites
 *   the author's markup with a border style of its own. It makes the damage worse and less visible.
 * - `code`, `video`, `formula`, `direction`: no occurrences in the imported corpus.
 */
const FORMAT_BLOTS = {
    align: AlignStyle,
    background: BackgroundStyle,
    blockquote: Blockquote,
    bold: Bold,
    color: ColorStyle,
    header: Header,
    image: Image,
    indent: IndentClass,
    italic: Italic,
    link: Link,
    list: List,
    script: Script,
    strike: Strike,
    underline: Underline
};

const FORMATS = Object.keys(FORMAT_BLOTS);

Quill.register(
    {
        ...Object.fromEntries(
            Object.entries(FORMAT_BLOTS).map(([name, blot]) => [`formats/${name}`, blot])
        ),
        'modules/toolbar': Toolbar,
        'themes/snow': SnowTheme
    },
    true
);

/**
 * What Clean removes. Everything the editor can hold, not just what the toolbar offers: a member
 * who presses Clean on imported text expects the colour and underline to go too, and those are
 * registered now. `image` is excluded because it is an embed rather than an attribute -- there is
 * no "off" value for it, and removing images is not what Clean means.
 */
const CLEANABLE_FORMATS = Object.keys(FORMAT_BLOTS).filter((format) => format !== 'image');

const TOOLBAR = [
    ['bold', 'italic'],
    ['link'],
    [{ list: 'bullet' }, { list: 'ordered' }],
    ['blockquote'],
    ['clean']
];

const INVALID_CLASS = 'chroa-discussion-board__rich-text--invalid';

/**
 * A structural fingerprint of a body: what survives editing, and what a reader would notice going
 * missing. Compared before and after the editor has loaded a post, it says whether saving would
 * lose something -- a table, a rule, an image -- without claiming a difference for the countless
 * harmless ways Quill rewrites markup it *can* represent.
 */
const signatureOf = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const attributes = (selector: string, attribute: string) =>
        [...doc.body.querySelectorAll(selector)]
            .map((el) => el.getAttribute(attribute) ?? '')
            .sort()
            .join('|');

    return [
        // All whitespace is dropped rather than collapsed: getSemanticHTML() joins block
        // elements with nothing between them, so a source with newlines between its <p> tags
        // would otherwise look like changed text on every post.
        doc.body.textContent?.replace(/\s+/g, '') ?? '',
        attributes('img', 'src'),
        attributes('a', 'href'),
        ['table', 'iframe', 'hr', 'figure'].filter((tag) => doc.body.querySelector(tag)).join(',')
    ].join('\u0000');
};

/**
 * Reveals the field's warning when the editor could not represent everything the post arrived
 * with. Nothing is destroyed unless the member deliberately edits -- and if they do, they were
 * told first.
 */
const warnIfLossy = (fieldEl: HTMLElement, originalHtml: string, editorHtml: string) => {
    const warningEl = fieldEl.querySelector<HTMLElement>('.chroa-discussion-board__rich-text-lossy');

    if (!warningEl) return;

    if (signatureOf(originalHtml) !== signatureOf(editorHtml)) warningEl.hidden = false;
};

/**
 * The editor belonging to each initialised field, so a caller holding only the field element can
 * reach it. Weak so a field removed by an SPA navigation takes its editor with it.
 */
const editorsByField = new WeakMap<HTMLElement, Quill>();

/**
 * Puts the caret in a field's body editor, for a visitor who arrived expecting to type.
 *
 * Nothing here scrolls: the caller has already positioned the page, and Quill's own scrolling is
 * why the selection is set silently.
 *
 * Each step down is a field that initialised less far than the one above, so a field Quill never
 * reached still takes the caret in its plain textarea rather than losing it altogether.
 *
 * @returns whether the caret could be placed
 */
export const focusRichTextField = (fieldEl: HTMLElement): boolean => {
    const quill = editorsByField.get(fieldEl);

    if (quill) {
        quill.root.focus({ preventScroll: true });

        // Without an explicit selection the caret lands at an arbitrary offset, which reads as the
        // editor having been clicked at random.
        quill.setSelection(quill.getLength(), 0, 'silent');

        return true;
    }

    const editableEl = fieldEl.querySelector<HTMLElement>('.ql-editor');

    if (editableEl) {
        editableEl.focus({ preventScroll: true });

        return true;
    }

    const textareaEl = fieldEl.querySelector<HTMLTextAreaElement>('textarea');

    if (textareaEl && !textareaEl.hidden) {
        textareaEl.focus({ preventScroll: true });

        return true;
    }

    return false;
};

/**
 * Edits HTML through Quill while retaining the textarea for submission and fallback.
 */
export const initRichTextField = (fieldEl: HTMLElement, gc: DisposalSet) => {
    if (fieldEl.dataset.chroaRichTextReady) return;

    const textareaEl = select<HTMLTextAreaElement>(fieldEl, 'textarea');
    const formEl = textareaEl.form;

    if (!formEl) return;

    // A composer opened before this script ran may hold the caret in the textarea, which the
    // editor is about to hide.
    const textareaHadFocus = document.activeElement === textareaEl;

    const editorEl = document.createElement('div');
    editorEl.className = 'chroa-discussion-board__editor';
    fieldEl.insertBefore(editorEl, textareaEl);

    const quill = new Quill(editorEl, {
        formats: FORMATS,
        modules: { toolbar: TOOLBAR },
        placeholder: textareaEl.placeholder || undefined,
        theme: 'snow'
    });

    const minTextLength = Number(fieldEl.dataset.chroaMinLength) || 0;
    const feedbackEl = fieldEl.querySelector<HTMLElement>(
        '.chroa-discussion-board__rich-text-feedback'
    );

    const originalValue = textareaEl.value;

    if (originalValue) {
        quill.clipboard.dangerouslyPasteHTML(originalValue, 'silent');

        // History listens on EDITOR_CHANGE, which Quill emits before it suppresses a silent
        // source, so loading the post leaves an undo entry that empties the editor. One reflexive
        // Ctrl+Z on an untouched edit form would wipe the body -- and set the dirty latch while
        // doing it, so the empty body would be what got saved.
        quill.history.clear();

        warnIfLossy(fieldEl, originalValue, quill.getSemanticHTML());
    }

    // Associate the textarea's label with the contenteditable editor.
    const editableEl = select<HTMLElement>(editorEl, '.ql-editor');
    const labelEl = textareaEl.labels?.[0];

    editableEl.setAttribute('aria-multiline', 'true');
    editableEl.setAttribute('role', 'textbox');

    if (labelEl) {
        if (!labelEl.id) labelEl.id = `${textareaEl.id}Label`;
        editableEl.setAttribute('aria-labelledby', labelEl.id);
    }

    // The feedback element is display:none until the field is invalid, so the field is marked
    // and described only while there is a message to read.
    const markInvalid = () => {
        editableEl.setAttribute('aria-invalid', 'true');

        if (feedbackEl?.id) editableEl.setAttribute('aria-describedby', feedbackEl.id);
    };

    const clearInvalid = () => {
        editableEl.removeAttribute('aria-invalid');
        editableEl.removeAttribute('aria-describedby');
    };

    const getNormalizedTextLength = () => quill.getText().replace(/\s+/g, ' ').trim().length;

    // Deliberately counts text only, with no allowance for an embed. DiscussionMessageValidator
    // makes the same judgement -- an image is not a substitute for saying something -- and a
    // guard that admitted what the server then refused would cost a page round trip to say so.
    const isEmpty = () => getNormalizedTextLength() === 0;

    /**
     * Set only by an edit the member actually made. Without it, merely opening a form -- or
     * navigating away from one -- would rewrite the stored body through the editor, which for a
     * post carrying markup the editor cannot represent means silently deleting it.
     *
     * Sound because a silent load provably emits no `text-change`. It starts set when the browser
     * restored an earlier draft into the textarea before this ran: the value then differs from the
     * body the server rendered, which is the textarea's default value.
     */
    let dirty = textareaEl.value !== textareaEl.defaultValue;

    // Present only on an edit form showing the stored body. Reporting it untouched lets the server
    // keep the stored body rather than the sanitized copy the form was loaded with, and skip a
    // save that changes nothing.
    const bodyEditedEl = fieldEl.querySelector<HTMLInputElement>('input[data-chroa-body-pristine]');

    // Store portable HTML without Quill-specific classes.
    const syncToTextarea = () => {
        if (bodyEditedEl) bodyEditedEl.value = String(dirty);

        if (!dirty) {
            textareaEl.value = originalValue;

            return;
        }

        // Quill escapes every space in a text run as &nbsp;, which cannot line-wrap and so
        // overflows its column in the read view. Safe to undo because `code-block` is not a
        // registered format, so no content here can legitimately hold one.
        textareaEl.value = isEmpty() ? '' : quill.getSemanticHTML().replace(/&nbsp;/g, ' ');
    };

    const onTextChange = (
        _delta: unknown,
        _oldDelta: unknown,
        source: (typeof Quill.sources)[keyof typeof Quill.sources]
    ) => {
        if (source === Quill.sources.USER) dirty = true;

        syncToTextarea();
        fieldEl.classList.remove(INVALID_CLASS);
        clearInvalid();
    };

    quill.on('text-change', onTextChange);

    // The stock handler rebuilds the selection from getText(), which has no characters for an
    // embed -- so select-all then Clean would delete every image in the post. Clearing only what
    // the toolbar can apply is a no-op for embeds.
    (quill.getModule('toolbar') as Toolbar).addHandler('clean', () => {
        CLEANABLE_FORMATS.forEach((format) => quill.format(format, false, Quill.sources.USER));
    });

    const required = textareaEl.required;
    const toolbar = quill.getModule('toolbar') as Toolbar;

    // Hidden controls cannot receive focus for native required-field validation.
    textareaEl.hidden = true;
    textareaEl.required = false;
    syncToTextarea();
    fieldEl.dataset.chroaRichTextReady = 'true';
    editorsByField.set(fieldEl, quill);

    if (textareaHadFocus) focusRichTextField(fieldEl);

    gc.register(() => {
        syncToTextarea();

        // The textarea is editable again, and nothing is watching it.
        if (bodyEditedEl) bodyEditedEl.value = 'true';

        quill.off('text-change', onTextChange);
        toolbar.container?.remove();
        editorEl.remove();
        textareaEl.hidden = false;
        textareaEl.required = required;
        fieldEl.classList.remove(INVALID_CLASS);
        delete fieldEl.dataset.chroaRichTextReady;
        editorsByField.delete(fieldEl);
    });

    gc.register(formEl).addListener('submit', (event) => {
        syncToTextarea();

        const textLength = getNormalizedTextLength();

        // Mirrors DiscussionMessageValidator exactly: a body is always required and always has to
        // clear the length floor. An attachment used to waive the floor here and on the server;
        // it no longer does on either side, so the same body gets the same answer whether or not
        // a file is staged.
        const required = Math.max(minTextLength, 1);

        if (textLength >= required) return;

        event.preventDefault();

        const message =
            textLength === 0
                ? fieldEl.dataset.chroaMessageRequired
                : fieldEl.dataset.chroaMessageTooShort;


        fieldEl.classList.add(INVALID_CLASS);
        markInvalid();

        // Written only once the element is shown: a live region mutated while it is still
        // display:none is not reliably announced.
        if (feedbackEl && message) feedbackEl.textContent = message;

        quill.focus();
    });
};
